import React from "react";
import { useTranslation } from "react-i18next";
import { Trophy } from "../../lib/trophies";
import { panelHeading } from "../../styles";

interface Props {
  trophies?: Trophy[];
  myName?: string | null;
}

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 12,
  padding: "1rem 1.25rem",
  marginBottom: "1rem",
};

const MAX_NAMED_RECIPIENTS = 3;

/** Names to actually render for one trophy: myName (if present in the list) sorted to the front
 * so it always survives truncation — you should always see your own name on a trophy you won —
 * then capped at MAX_NAMED_RECIPIENTS, with the remainder collapsed to a count. Array.sort is
 * stable in every supported engine, so the other names keep their existing order. */
function visibleRecipients(recipients: string[], myName?: string | null): { shown: string[]; hidden: number } {
  const ordered = myName
    ? [...recipients].sort((a, b) => (a === myName ? -1 : b === myName ? 1 : 0))
    : recipients;
  const shown = ordered.slice(0, MAX_NAMED_RECIPIENTS);
  return { shown, hidden: ordered.length - shown.length };
}

export default function TrophiesPanel({ trophies, myName }: Props) {
  const { t } = useTranslation();

  if (!trophies || trophies.length === 0) return null;

  return (
    <div style={panelStyle}>
      <h2 style={panelHeading}>{t("league.trophiesPanel.title")}</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {trophies.map((trophy, i) => {
          const { shown, hidden } = visibleRecipients(trophy.recipients, myName);
          return (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text)" }}>{trophy.name}</span>
                <span style={{ fontSize: "0.68rem", color: "var(--color-text-dim)" }}>{trophy.description}</span>
              </div>
              <span style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>
                {shown.map((name, j) => (
                  <React.Fragment key={j}>
                    {j > 0 && ", "}
                    <span style={myName && name === myName ? { color: "var(--color-accent)", fontWeight: 600 } : undefined}>{name}</span>
                  </React.Fragment>
                ))}
                {hidden > 0 && <>{shown.length > 0 && " "}{t("league.trophiesPanel.andNMore", { count: hidden })}</>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
